import { PlusCircleOutlined } from '@ant-design/icons';
import {
  Button,
  message,
  Space,
  Switch,
  Table,
  TablePaginationConfig,
  Typography,
} from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { Column, ContentFilters } from 'components';
import {
  StyledTableHeader,
  StyledTableWrapper,
} from 'components/table/table.styled';
import dayjs from 'dayjs';
import { useGetManageUserList } from 'features/assigned-clients/use-cases/get-manage-user';
import { UserFilterFormsValues } from 'features/users/components/users-filter-form';
import { useStatusChange } from 'features/users/use-cases/update-status';
import { usePaginationParams } from 'hooks';
import { omit } from 'lodash';
import { FilePenLine } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { formatDate } from 'utils/date';

import { EditManageUser } from './edit-manage-user';
import { UsersClientsFilterForm } from './form-filter/user-clients-filter';
import { AddEditUserModal } from './modal/add-edit-user';

export const ManageUser = ({ assignvalue }: any) => {
  const { id } = useParams();
  const getInitialFilterValues: UserFilterFormsValues = {
    Name: undefined,
    RoleName: undefined,
    Email: undefined,
    IsActive: undefined,
    CreatedDate: undefined,
    date: [undefined, undefined],
  };

  const { params, updateParams, resetPage } = usePaginationParams<any>({
    PageIndex: 1,
    RecordPerPage: 10,
    ...getInitialFilterValues,
  });

  const [loadingStatus, setLoadingStatus] = useState<Record<string, boolean>>(
    {},
  );
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [userData, setUserData] = useState(null);

  const { mutate: changeStatus } = useStatusChange();

  const preparePayload: any = (paramsw: any) => {
    const createdAtFrom = paramsw.date?.[0];
    const createdAtTo = paramsw.date?.[1];

    return {
      ...omit(params, ['date']),
      CreatedDate: paramsw?.CreatedDate
        ? dayjs(paramsw.CreatedDate).format('YYYY-MM-DD')
        : null,
      LastUpdateFrom: createdAtFrom
        ? formatDate({
            date: dayjs(createdAtFrom).startOf('day'),
            format: 'isoDate',
          })
        : undefined,
      LastUpdateTo: createdAtTo
        ? formatDate({
            date: dayjs(createdAtTo).startOf('day'),
            format: 'isoDate',
          })
        : undefined,
    };
  };
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    updateParams({
      ...params,
      PageIndex: pagination.current,
      RecordPerPage: pagination.pageSize,
      order_by: Array.isArray(sorter)
        ? undefined
        : sorter.order === 'ascend'
        ? 'name'
        : '-name',
    });
  };
  const {
    data: dataList,
    isFetching,
    error,
    refetch,
  } = useGetManageUserList({
    ...preparePayload(params),
    CompanyId: id,
  });

  if (error) {
    message.error('Failed to load user list');
  }

  const handleFiltersChange = (values: UserFilterFormsValues) => {
    updateParams(values);
    resetPage();
  };

  const handleStatusChange = (userId: string, currentStatus: boolean) => {
    const updatedStatus = !currentStatus;
    const formData = {
      UserId: userId,
      Status: updatedStatus,
      CompanyId: id,
    };

    setLoadingStatus((prev) => ({ ...prev, [userId]: true }));
    changeStatus(formData, {
      onSuccess: () => {
        message.success('Status updated successfully');
        refetch();
        setLoadingStatus((prev) => ({ ...prev, [userId]: false }));
      },
      onError: () => {
        message.error('Error updating status');
        setLoadingStatus((prev) => ({ ...prev, [userId]: false }));
      },
    });
  };
  useEffect(() => {
    if (assignvalue == 6) {
      refetch();
    }
  }, [assignvalue]);

  return (
    <Fragment>
      <ContentFilters formId='users-filters-form'>
        <UsersClientsFilterForm
          formId='users-filters-form'
          handleFilter={handleFiltersChange}
          initialValues={getInitialFilterValues}
        />
      </ContentFilters>

      <StyledTableWrapper className='card grid_secton'>
        <StyledTableHeader className='gs_head'>
          <h3>
            User List
            {/* <span>{dataList?.totalCount || '0'}</span> */}
          </h3>
          <div className='buttongroup'>
            <Button
              icon={<PlusCircleOutlined />}
              style={{ backgroundColor: '#223666', color: 'white' }}
              onClick={() => setShowAddModal(true)}
              disabled={isFetching} // Prevent adding new users while loading
            >
              Add New User
            </Button>
            {showAddModal && (
              <AddEditUserModal onClose={() => setShowAddModal(false)} />
            )}
          </div>
        </StyledTableHeader>
        <div className='cstmtblbx w-full'>
          <div className='cstm_table'>
            <Table
              loading={isFetching}
              dataSource={dataList?.rc}
              rowKey='Id'
              onChange={handleTableChange}
              pagination={{
                current: params.PageIndex,
                defaultPageSize: params.RecordPerPage,
                total:
                  dataList?.rc && dataList?.rc?.length > 0
                    ? dataList?.rc[0]?.TotalRecords
                    : 0,
              }}
            >
              <Column
                title={'Sr No'}
                dataIndex={'Sr No'}
                render={(_, record, index) =>
                  (params.PageIndex - 1) * params.RecordPerPage + index + 1
                }
              />
              <Column title='User Name' dataIndex='Name' />
              <Column title='Email' dataIndex='Email' />
              <Column title='User Role' dataIndex='RoleName' />
              <Column
                title='Date Added'
                dataIndex='CreatedDate'
                render={(CreatedDate) =>
                  dayjs(CreatedDate).format('DD MMM YYYY')
                }
              />
              <Column
                title={'Last Update'}
                dataIndex={'LastUpdated'}
                render={(_, record: any) =>
                  dayjs(record.UpdateDate).format('DD MMM YYYY')
                }
              />
              <Column
                title='User Type'
                dataIndex='IsPrimary'
                render={(IsPrimary) => (
                  <Typography>
                    {IsPrimary == false ? 'Staff' : 'Client'}
                  </Typography>
                )}
              />
              <Column
                title='Status'
                dataIndex='IsActive'
                render={(isActive) => (
                  <Typography style={{ color: isActive ? '#155724' : 'red' }}>
                    {isActive ? 'Active' : 'Inactive'}
                  </Typography>
                )}
              />
              <Column
                width='135px'
                title='Action'
                render={(_, record: any) => (
                  <Space className='grdbtngroup'>
                    {/* {record?.IsPrimary == false && ( */}
                    <Button
                      className='btn'
                      type='link'
                      onClick={() => {
                        setShowModal(true);
                        setUserData(record);
                      }}
                    >
                      <FilePenLine height={16} />
                    </Button>
                    {/* )} */}
                    {/* <Popconfirm
                      title='Delete the user?'
                      okText='Yes'
                      cancelText='No'
                    >
                      <Button className='btn' type='link'>
                        <Trash2 stroke='#E04343' height={16} />
                      </Button>
                    </Popconfirm> */}
                    <Switch
                      disabled={loadingStatus[record.Id]}
                      checked={record.IsActive}
                      onChange={() =>
                        handleStatusChange(record.Id, record.IsActive)
                      }
                    />
                  </Space>
                )}
              />
            </Table>
          </div>
        </div>
      </StyledTableWrapper>

      {showModal && (
        <EditManageUser
          onClose={() => setShowModal(false)}
          userData={userData}
          refetch={refetch}
        />
      )}
    </Fragment>
  );
};
